import { EmbeddableFlowDataQualityWarning } from '@tableau/data-catalog-client-components';
import { SiteUrls } from '@tableau/shared-urls';
import React from 'react';

import { dataQualityIndicatorInfoToDataQualityIndicator } from '~src/catalog/models/IDataQualityIndicatorInfo';
import { ModifiedDateMetadata, OwnerMetadata } from '~src/spacesPlatform/components/header/HeaderMetadataRow';
import { ActionsMenuIcon, FavoriteToggleWithCountsIcon } from '~src/spacesPlatform/components/header/HeaderNameRowIcons';
import { ConnectedSpaceHeader } from '~src/spacesPlatform/components/header/SpaceHeader';
import { SpaceHeaderStyles } from '~src/spacesPlatform/components/header/SpaceHeaderStyles';

import { SiteUrlsProps } from '../../app/selectors/SiteUrlsSelectors';
import { ConnectedExtractMetadata } from '../../app/spaces/Header/ExtractMetadata';
import { CatalogApiRequests } from '../../catalog/CatalogApiRequests';
import { catalogApolloClient } from '../../catalog/CatalogApolloClient';
import { catalogFeatureFlagsAdapter } from '../../catalog/CatalogFeatureFlagsAdapter';
import { CERTIFIED_INDICATOR_TYPE, dataQualityIndicatorInfosToProps } from '../../catalog/components/dataQuality/DataQualityType';
import { selectCatalogingOnSiteEnabled } from '../../catalog/selectors/CatalogSelectors';
import { DataCatalogUIActionsService } from '../../catalog/services/DataCatalogUIActionsService';
import { FeatureFlags } from '../../ts/FeatureFlags';
import { ISpaceItemDetailInfo } from '../../ts/models/ISpaceItemDetailInfo';
import { EditFlowEntityAction } from '../entityActions/EditFlowEntityAction';
import { FlowAllowedActions } from '../serverApi/types/FlowAllowedActions';
import { FlowSpaceBanner } from './FlowSpaceBanner';

export interface FlowSpaceHeaderProps extends SiteUrlsProps {
  readonly item: ISpaceItemDetailInfo<'flow'>;
  readonly availableActions: FlowAllowedActions;
  readonly catalogingOnSiteEnabled: boolean;
  readonly addToFavorites: () => void;
  readonly removeFromFavorites: () => void;
}

export function FlowSpaceHeader(props: FlowSpaceHeaderProps): JSX.Element {
  const shouldShowDraftBanner = FeatureFlags.flowDraftsEnabled && props.item && props.item.flowDraft;
  return <ConnectedSpaceHeader
    item={props.item}
    availableActions={props.availableActions}
    nameRowIcons={
      <React.Fragment>
        {FavoriteToggleWithCountsIcon({
          item: props.item,
          addToFavorites: props.addToFavorites,
          removeFromFavorites: props.removeFromFavorites,
        })}
        {ActionsMenuIcon(props.item)}
      </React.Fragment>
    }
    metadataRow={
      <React.Fragment>
        {OwnerMetadata(props.item.owner, props.urls)}
        {ModifiedDateMetadata(props.item.updatedAt)}
        <ConnectedExtractMetadata itemInfo={props.item} availableActions={props.availableActions} />
        {selectCatalogingOnSiteEnabled &&
          <DataQualityMetadata flowInfo={props.item} urls={props.urls} />}
      </React.Fragment>
    }
    spotlightActions={shouldShowDraftBanner ? [] : [EditFlowEntityAction]}
    bottomElement={<FlowSpaceBanner item={props.item}/>}
  />;
}

function DataQualityMetadata(props: {
  flowInfo: ISpaceItemDetailInfo<'flow'>;
  urls: SiteUrls;
}): JSX.Element | null {

  const anchoredWarnings = props.flowInfo.dataQualityIndicators.filter(t => t.type !== CERTIFIED_INDICATOR_TYPE);
  const dataQualityWarningsOnThisItem = dataQualityIndicatorInfosToProps(
    {
      assetUrl: props.urls.flowUrl(props.flowInfo.id),
      assetName: props.flowInfo.name,
      assetType: 'Flow',
      onContentItem: true
    },
    props.flowInfo.dataQualityIndicators
  );

  return (
    <span className={SpaceHeaderStyles.TextOnlyMetadataLabelWithLeftMargin}>
      <EmbeddableFlowDataQualityWarning
        client={catalogApolloClient}
        flowVizportalUrlId={props.flowInfo.id}
        ownContentWarnings={dataQualityWarningsOnThisItem}
        siteUrls={props.urls}
        apiRequests={CatalogApiRequests}
        uiActions={DataCatalogUIActionsService.instance()}
        featureFlags={catalogFeatureFlagsAdapter()}
        asset={{
          assetName: props.flowInfo.name,
          assetType: 'Flow',
          assetUrl: urls => urls.flowUrl(props.flowInfo.id)
        }}
        anchoredWarnings={anchoredWarnings.map(i => dataQualityIndicatorInfoToDataQualityIndicator(i))}
      />
    </span>
  );
}
